<template>
  <VDropdown
    :id="id"
    :autoHide="false"
    :triggers="[]"
    :shown="shown"
    :disabled="!shown"
    :placement="placement"
    :skidding="skidding"
    :distance="distance"
    class="learn-tooltip"
    :class="[
      { 'is-circle': isCircle },
      { 'is-active': step === selectedStep && shown },
      { 'is-body-bg': isBodyBg },
      `indentation-${indentationAroundElement}`
    ]"
    theme="info-dropdown"
    :popperClass="`learn-tooltip__popper ${popperClass}`"
    @applyShow="onShowDropdown"
    @applyHide="$emit('onHideDropdown')"
  >
    <slot name="tooltip-label"> </slot>

    <template #popper="{ hide }">
      <slot
        name="tooltip-content"
        :hide="hide"
      >
        <div class="learn-tooltip__content">
          <span
            v-if="tooltipText"
            class="learn-tooltip__content__title"
          >
            {{ tooltipText }}
          </span>
          <span
            v-if="tooltipSubText"
            class="learn-tooltip__content__sub-title"
          >
            {{ tooltipSubText }}
          </span>
        </div>
      </slot>
      <div class="learn-tooltip__footer">
        <div class="learn-tooltip__counter-wrapper">
          <slot name="before-counter" />
          <slot name="counter">
            <span
              v-if="maxStep > 1"
              class="learn-tooltip__step"
            >
              {{ selectedStepNumber }} of {{ maxStep }}
            </span>
            <span v-else> &nbsp; </span>
          </slot>
          <slot name="after-counter" />
        </div>
        <div class="learn-tooltip__btns">
          <button
            v-if="selectedStepNumber > 1"
            class="learn-tooltip__next"
            @click="onPrev"
          >
            <BaseSubHeading
              size="medium"
              weight="500"
              color="--monochrome-00"
              darkColor="--monochrome-07"
            >
              Prev
            </BaseSubHeading>
          </button>
          <div
            v-if="selectedStepNumber < maxStep && selectedStepNumber > 1"
            class="learn-tooltip__divider"
          ></div>
          <button
            v-if="selectedStepNumber < maxStep"
            class="learn-tooltip__next"
            @click="onNext"
          >
            <BaseSubHeading
              size="medium"
              weight="500"
              color="--monochrome-00"
              darkColor="--monochrome-07"
            >
              Next
            </BaseSubHeading>
          </button>
          <div
            v-if="selectedStepNumber === maxStep && maxStep > 1"
            class="learn-tooltip__divider"
          ></div>
          <button
            v-if="selectedStepNumber === maxStep"
            class="learn-tooltip__next"
            type="button"
            @click="onNext"
          >
            <BaseSubHeading
              size="medium"
              weight="500"
              color="--monochrome-00"
              darkColor="--monochrome-07"
            >
              Close
            </BaseSubHeading>
          </button>
        </div>
        <Teleport to="body">
          <div
            v-if="showOverlay"
            class="learn-tooltip__overlay"
          />
        </Teleport>
      </div>
    </template>
  </VDropdown>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import BaseSubHeading from "@/components/Base/BaseSubHeading.vue"

import { TipsStepType, UserSettings } from "@/models"
import { Actions, Getters } from "@/store"

type IndentationAroundElement = "lg" | "md" | "sm" | "none"

@Options({
  name: "TooltipLearn",
  components: { BaseSubHeading }
})
export default class extends Vue {
  @Prop({ default: "" }) readonly step: TipsStepType
  @Prop({ default: "bottom" }) readonly placement: string
  @Prop({ default: "none" }) readonly indentationAroundElement: IndentationAroundElement
  @Prop({ default: false }) readonly isCircle: boolean
  @Prop({ default: false }) customHide: boolean
  @Prop({ default: false }) isBodyBg: boolean
  @Prop({ default: 0 }) readonly skidding: number
  @Prop({ default: 0 }) readonly distance: number
  @Prop({ default: true }) readonly show: boolean
  @Prop({ default: false }) readonly hideOverlay: boolean
  @Prop({ default: "" }) readonly tooltipText: string
  @Prop({ default: "" }) readonly tooltipSubText: string
  @Prop({ default: "" }) readonly popperClass: string
  @Prop({ default: "" }) readonly id: string

  @Getter(Getters.GET_SELECTED_TIPS_STEP) readonly selectedStep: TipsStepType | null
  @Getter(Getters.GET_TIPS_STEPS) readonly steps: TipsStepType[]
  @Getter(Getters.GET_USER_CURRENT_PROFILE_SETTINGS) readonly userSettings: UserSettings

  @Action(Actions.UPDATE_PROFILE_SETTINGS) updateProfileSettings: ({
    settings,
    immediate
  }: {
    settings: UserSettings
    immediate?: boolean
  }) => void

  @Action(Actions.SET_SELECTED_STEP) setSelectedStep: (step: TipsStepType | null) => void

  get maxStep() {
    return this.steps.length
  }

  get selectedStepNumber() {
    return this.steps.findIndex(step => step === this.step) + 1
  }

  get shown() {
    return this.selectedStep && this.step === this.selectedStep && !this.customHide && this.show
  }

  get showOverlay() {
    return this.step === this.selectedStep && !this.customHide && this.show && !this.hideOverlay
  }

  onNext() {
    if (this.selectedStepNumber <= 0) return
    const shownTips: TipsStepType[] = this.userSettings?.shownTips ?? []
    this.updateProfileSettings({
      settings: {
        ...this.userSettings,
        shownTips: [...shownTips, this.step]
      },
      immediate: true
    })
    const nextStep = this.steps[this.selectedStepNumber] ?? null
    this.setSelectedStep(nextStep)
  }

  @Emit()
  onShowDropdown() {
    const activeElement = document.activeElement as HTMLElement
    if (activeElement) {
      activeElement.blur()
    }
  }

  onPrev() {
    if (this.selectedStepNumber <= 1) return
    const prevStep = this.steps[this.selectedStepNumber - 2]
    if (!prevStep) return
    const shownTips: TipsStepType[] = this.userSettings?.shownTips ?? []
    this.updateProfileSettings({
      settings: {
        ...this.userSettings,
        shownTips: shownTips.filter(step => step !== this.step)
      },
      immediate: true
    })
    this.setSelectedStep(prevStep)
  }
}
</script>
